import type {
  HealthResponse,
  ApiConfig
} from './types'
import { config as appConfig } from '../config'

/**
 * Custom error class for API errors that includes response data.
 * Used to pass HTTP status and response body to error handlers.
 */
export class ApiError extends Error {
  response: {
    status: number;
    data: unknown;
  };

  constructor(message: string, status: number, data: unknown) {
    super(message);
    this.name = 'ApiError';
    this.response = { status, data };
  }
}

/**
 * Auth context accessor for API client.
 * Set by App.tsx after auth context is initialized.
 */
interface AuthContextAccessor {
  getAccessToken: () => Promise<string>;
  isAuthenticated: boolean;
}

class ApiServiceClass {
  private config: ApiConfig = {
    baseUrl: appConfig.backendUrl,
    timeout: appConfig.apiTimeout
  }

  private authContext: AuthContextAccessor | null = null;
  private tenantId: string | null = null;

  /**
   * Set authentication context for API requests.
   * Should be called by App.tsx after AuthProvider is initialized.
   */
  setAuthContext(authContext: AuthContextAccessor): void {
    this.authContext = authContext;
  }

  /**
   * Set tenant context for API requests.
   * Can be updated when tenant selection changes.
   */
  setTenantContext(tenantId: string | null): void {
    this.tenantId = tenantId;
  }

  /**
   * Get headers for API requests.
   * Includes Authorization Bearer token and X-Tenant-ID when available.
   */
  private async getHeaders(): Promise<Record<string, string>> {
    const headers: Record<string, string> = {};

    // Add Authorization header with Bearer token if authenticated
    if (this.authContext?.isAuthenticated) {
      try {
        const token = await this.authContext.getAccessToken();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to get access token:', error);
        // Continue without auth header - request will fail with 401 if auth required
      }
    }

    // Add tenant context header if available
    if (this.tenantId) {
      headers['X-Tenant-ID'] = this.tenantId;
    }

    return headers;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit = {},
    isRetry = false
  ): Promise<T> {
    const url = `${this.config.baseUrl}${endpoint}`

    // Get headers with Bearer token
    const baseHeaders = await this.getHeaders();

    // Only set Content-Type for requests with JSON body
    const headers: Record<string, string> = {
      ...baseHeaders,
      ...options.headers as Record<string, string>
    }
    if (options.body && typeof options.body === 'string') {
      headers['Content-Type'] = 'application/json'
    }

    const response = await fetch(url, {
      ...options,
      headers,
      // Remove credentials: 'include' - no longer using cookies
    })

    if (!response.ok) {
      const errorText = await response.text()
      let errorMessage = `HTTP error! status: ${response.status}`
      let errorData: unknown = null;

      try {
        const errorJson = JSON.parse(errorText)
        errorData = errorJson; // Store full response data for ApiError
        if (errorJson.detail) {
          // Handle FastAPI validation errors (detail is an array of error objects)
          if (Array.isArray(errorJson.detail)) {
            errorMessage = errorJson.detail
              .map((err: { msg?: string; loc?: string[] }) => {
                const location = err.loc ? err.loc.join('.') : '';
                return `${location}: ${err.msg || 'Validation error'}`;
              })
              .join(', ');
          }
          // Handle object detail
          else if (typeof errorJson.detail === 'object') {
            errorMessage = JSON.stringify(errorJson.detail);
          }
          // Handle string detail
          else {
            errorMessage = String(errorJson.detail);
          }
        }
      } catch {
        // If not JSON, use the text as error message
        if (errorText) {
          errorMessage = errorText
        }
      }

      // Handle 401 Unauthorized with automatic token refresh retry
      if (response.status === 401 && !isRetry && this.authContext?.isAuthenticated) {
        try {
          // Force token refresh by calling getAccessToken again
          // MSAL will automatically refresh the token if needed
          await this.authContext.getAccessToken();

          // Retry the request once with refreshed token
          return this.request<T>(endpoint, options, true);
        } catch (refreshError) {
          // eslint-disable-next-line no-console
          console.error('Token refresh failed:', refreshError);
          // Fall through to throw original error
        }
      }

      throw new ApiError(errorMessage, response.status, errorData)
    }

    // Handle 204 No Content and other responses with no body
    if (response.status === 204 || response.headers.get('content-length') === '0') {
      return undefined as T;
    }

    // Check if response has content before parsing JSON
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return response.json();
    }

    // For non-JSON responses or empty responses, return undefined
    return undefined as T;
  }

  async checkHealth(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health')
  }
}

export const ApiService = new ApiServiceClass()
